import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Question } from '../../interfaces/meta/question';
import { ExternalInfluencerService } from '@ui/company-page';
import { RealEstateGroupService } from '@app/real-estate-group/services/real-estate-group.service';
import { GroupedSearchableDropdownInputOptions, SearchableDropdownInputOption } from '@ui/form';
import { delay, first, map, switchMap, take, takeUntil, tap } from 'rxjs';
import { ArrayUtils, DbUtils, RealEstateAgentUtils } from '@core/utils';
import { CustomDatabaseValue } from '@core/database';
import { RealEstateAgentsGroupedByLocationDto } from '@app/real-estate-agent/interfaces/real-estate-agents-grouped-by-location';
import { RxjsComponent } from '@core/rxjs';
import { buildLocationsOptions } from '../../constants/surveys.constants';

@Component({
    selector: 'app-question-contact-person-component',
    templateUrl: './question-contact-person.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionContactPersonComponent extends RxjsComponent implements OnInit {
    @Input() public question: Question;
    @Input() public group: FormGroup;

    public label: string;
    public locationOptions: SearchableDropdownInputOption[] = [];
    public agentOptions: GroupedSearchableDropdownInputOptions[] = [];
    public agentOptionsOthers: SearchableDropdownInputOption[] = [];

    constructor(
        public translateService: TranslateService,
        private externalInfluencerService: ExternalInfluencerService,
        private realEstateGroupService: RealEstateGroupService
    ) {
        super()
    }

    public ngOnInit(): void {
        this.group
            .get('location.value')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((locationId) => {
                this.setRealEstateAgentOptions(locationId);
            });

        this.setRealEstateAgentOptions();
        this.setLocationsOptions();
        this.label =
            `<h6>${this.question.title[this.translateService.currentLang]}</h6>` +
            (this.question.subLabel
                ? ` <span class="u-color-muted u-font-size-small">${this.question.subLabel[this.translateService.currentLang]}</span>`
                : '');
    }

    private setLocationsOptions(): void {
        this.externalInfluencerService.externalInfluencer$.pipe(take(1)).subscribe((influencer) => {
            this.locationOptions = buildLocationsOptions(influencer.realEstateGroupLocations);
            if (ArrayUtils.getLength(this.locationOptions) === 1) {
                this.group.get('location.value').patchValue(ArrayUtils.first(this.locationOptions).value);
            }
        });
    }

    private setRealEstateAgentOptions(locationId?: string): void {
        const selectedOptions = this.group.get('linkedRealEstateAgents.value')?.value;
        this.externalInfluencerService.externalInfluencer$
            .pipe(
                first((influencer) => !!influencer?.realEstateGroup),
                map((influencer) => DbUtils.getStringId(influencer.realEstateGroup)),
                switchMap((realEstateGroupId: string) =>
                    this.realEstateGroupService.getRealEstateAgentsGroupedByLocation(
                        realEstateGroupId,
                        locationId && locationId !== CustomDatabaseValue.Other ? locationId : null
                    )
                ),
                tap((agentsGroupedByLocations: RealEstateAgentsGroupedByLocationDto) => {
                    this.agentOptionsOthers = agentsGroupedByLocations.otherRealEstateAgents.map(
                        RealEstateAgentUtils.mapRealEstateAgentToOption
                    );
                    this.agentOptions = agentsGroupedByLocations.groupedByLocationRealEstateAgents.map(
                        RealEstateAgentUtils.mapGroupedRealEstateAgentsToGroupedOptions
                    );
                }),
                delay(0), // needed to make sure all the options have been rendered
                tap(() => {
                    if (selectedOptions) this.group.get('linkedRealEstateAgents.value').patchValue(selectedOptions);
                })
            )
            .subscribe();
    }
}
